import { Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DriverOperationsService, FilterService } from '../../../../_shared/services';
import { AccountFilterData, WidgetFilterData } from '../../../../_shared/models';
import { BaseWidgetComponent } from '../../../../home/home-widgets/widgets/base-widget.component';
import { PaginationRequest } from '../../../../_shared/models/pagination-request.model';
import { endOfDay, startOfDay } from 'date-fns';
import { OperationStatusDetail } from '../../models/operation-status-detail.model';
import { DriverLocation } from '../../../../_shared/models/drivers/maps/driver-location.model';
import { TruncatetextPipe } from '../../../../_shared/pipes/truncatetext.pipe';
import { AvatarComponent } from "../../../../_shared/components/avatar/avatar.component";
import { TimeStatusIndicatorComponent } from "../../../../_shared/components/time-status-indicator/time-status-indicator.component";
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { CommonMapService } from '../../../../_shared/services/map/common-map.service';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { getDriverStatusLabelKey } from '../../helpers/driver-status-label.helper';

@Component({
  selector: 'app-driver-status-table',
  standalone: true,
  imports: [RouterModule, TruncatetextPipe, AvatarComponent, TimeStatusIndicatorComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './driver-status-table.component.html',
  styleUrl: './driver-status-table.component.scss',
})
export class DriverStatusTableComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  @Output() loadingChange = new EventEmitter<boolean>();

  private driverOperationsService = inject(DriverOperationsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private mapDataService = inject(CommonMapService);

  public drivers: any = [];
  public override isLoading: boolean = false;

  currentDate: Date = new Date();
  public paginatedDateFilterRequest: PaginationRequest = {
    accountIds: [],
    timeFrom: startOfDay(this.currentDate),
    timeTo: endOfDay(this.currentDate),
    page: 1,
    pageSize: 4
  };

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
  }

  ngOnInit(): void {
    this.mapDataService.updateData([]);
    this.isLoading = true;
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.paginatedDateFilterRequest.accountIds = this.widgetFilter.accountIds;
    await this.getDriverStatusDetailAsync();
  }

  public override formatValue(value: number): string {
    return `${value.toLocaleString()}`;
  }

  onAccountFilterChange(filterData: AccountFilterData) {
    this.filterService.updateFilter(filterData as WidgetFilterData);
  }

  async getDriverStatusDetailAsync() {
    this.isLoading = true;
    this.loadingChange.emit(this.isLoading);

    try {
      this.cancelSubject.next();
      const [detailsResponse, locationsResponse] = await Promise.all([
        this.driverOperationsService.getDriverStatusDetail(this.paginatedDateFilterRequest, this.cancelSubject),
        this.driverOperationsService.getDriversLocation(this.paginatedDateFilterRequest, this.cancelSubject),
      ]);

      this.drivers = detailsResponse.data?.results.map((driver: OperationStatusDetail) => ({
        name: `${driver.firstName} ${driver.lastName}`,
        phoneNumber: driver?.phoneNumber,
        vehicle: driver.assetName,
        image: driver.profilePictureUrl,
        signIn: { time: driver?.signIn ? driver.signIn.toString() : 'N/A', status: 'warning' },
        inVehicle: { time: driver?.inVehicle ? driver.inVehicle.toString() : 'N/A', status: 'success' },
        status: driver?.status ?? 'Unknown',
        statusKey: getDriverStatusLabelKey(driver?.status ?? 'Unknown'),
        lat: driver?.lat,
        lng: driver?.lng,
        heading: driver?.heading,
        location: driver?.location,
        startTime: driver?.startTime,
        yardCheckInThreshold: driver?.yardCheckInThreshold,
        vehicleInYardThreshold: driver?.vehicleInYardThreshold
      })) ?? [];

      const driverLocations: DriverLocation[] = locationsResponse.data?.filter((driver: OperationStatusDetail) => driver.lat !== null && driver.lng !== null)
        .map((driver: OperationStatusDetail) => ({
          driverName: `${driver.firstName} ${driver.lastName}`,
          phoneNumber: driver.phoneNumber,
          vehicle: driver.assetName ?? '-',
          status: driver.status ?? 'Unknown',
          lat: driver.lat,
          lng: driver.lng,
          heading: driver.heading,
          location: driver.location
        })
      ) as DriverLocation[] ?? [];
      this.mapDataService.updateData(driverLocations);

      this.isLoading = false;
      this.loadingChange.emit(this.isLoading);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.loadingChange.emit(this.isLoading);
        }
      }
      );
    }
  }

  public getStatusClass(status: string): string {
    return `bg-${status}`;
  }
}